import React from 'react';

import Infotip from 'App/common/Tooltip/Infotip/infotip';
import { extLnkMyPostRedirectV2 } from 'config/track-config';
import { RedirectStatus } from 'models/shipments';
import { trackInteraction } from 'store/analytics/analytics.thunks';
import { useAppDispatch } from 'store/store';

import { AppLocation } from '../../../../../utils/window.utils';
import RedirectModal from '../../../common/RedirectModal/RedirectModal';
import { useCurrentArticle } from '../../../hooks/shipment.hooks';
import { REDIRECT_FAILED, REDIRECT_INELIGIBLE, REDIRECT_LINK_ADDRESS_TEST_ID } from '../../DetailsPanel.test-ids';

import styles from './RedirectInfo.module.scss';

export const RedirectInfo: React.FC = () => {
  const dispatch = useAppDispatch();
  const currentArticle = useCurrentArticle();
  const { articleRedirect, redirectStatus, redirectIneligibility } = currentArticle || {};

  const [isRedirectModalOpen, setIsRedirectModalOpen] = React.useState(false);

  const redirectIneligibilityCode = redirectIneligibility?.code ?? null;
  const failedRedirect = redirectStatus === RedirectStatus.REDIRECT_FAILED;

  if (failedRedirect)
    return (
      <span className={styles.textRedirect} data-testid={REDIRECT_FAILED}>
        Couldn&apos;t be redirected<Infotip floatClassName={styles.redirectInfotip}>{failedRedirectInfotip}</Infotip>
      </span>
    );

  if (!articleRedirect) {
    let redirectIneligibleInfotip: React.ReactNode;
    switch (redirectIneligibilityCode) {
      case '1':
        redirectIneligibleInfotip = senderOptOut;
        break;
      case '2':
        redirectIneligibleInfotip = internationlOutbound;
        break;
      case '4':
        redirectIneligibleInfotip = tooLate;
        break;
      case '5':
        redirectIneligibleInfotip = parcelLocker;
        break;
      case '6':
        redirectIneligibleInfotip = parcelCollect;
        break;
      case '7':
        redirectIneligibleInfotip = poBox;
        break;
      case 'X':
        redirectIneligibleInfotip = noReceiverInfo;
        break;
      case 'Y':
        redirectIneligibleInfotip = articleDoesntMatch;
        break;
      default:
    }
    return (
      <span className={styles.textRedirect} data-testid={REDIRECT_INELIGIBLE}>
        Can&apos;t be redirected
        {redirectIneligibleInfotip ? (
          <Infotip floatClassName={styles.redirectInfotip}>{redirectIneligibleInfotip}</Infotip>
        ) : null}
      </span>
    );
  }

  return (
    <div className={styles.redirectRow}>
      <button
        className={styles.textRedirectLinkButton}
        data-testid={REDIRECT_LINK_ADDRESS_TEST_ID}
        onClick={() => {
          dispatch(trackInteraction('request redirect from details'));
          setIsRedirectModalOpen(true);
        }}
        aria-label='Redirect to a different address. Navigates away from the current page.'
      >
        Redirect
      </button>
      <RedirectModal
        isOpen={isRedirectModalOpen}
        handleClose={() => setIsRedirectModalOpen(false)}
        handleContinue={() => AppLocation.assign(`${extLnkMyPostRedirectV2}/${currentArticle.articleId}`)}
      />
    </div>
  );
};

const senderOptOut = <p>The sender chose not to allow redirection for this parcel.</p>;

const internationlOutbound = (
  <>
    <p>This parcel is being delivered overseas, so we can&apos;t redirect it.</p>
    <p>Once it arrives in the destination country, the local postal carrier may offer this option.</p>
  </>
);

const tooLate = <p>It&apos;s too late in this parcel&apos;s journey for us to redirect it.</p>;

const parcelLocker = (
  <p>This parcel has been addressed to a Parcel Locker, so it can&apos;t be redirected along the way.</p>
);

const parcelCollect = (
  <p>This parcel has been addressed to a Post Office, so it can&apos;t be redirected along the way.</p>
);

const poBox = <p>This parcel has been addressed to a Post Office Box, so it can&apos;t be redirected along the way.</p>;

const noReceiverInfo = (
  <p>The sender hasn&apos;t given us all the delivery details for this parcel, so it can&apos;t be redirected.</p>
);

const articleDoesntMatch = (
  <p>
    The delivery details for this parcel don&apos;t match your MyPost account details, so you won&apos;t be able to
    redirect it.
  </p>
);

const failedRedirectInfotip = (
  <>
    <p>
      This parcel couldn&apos;t be redirected, so the previous address is still shown in our system and on the
      parcel&apos;s label.
    </p>
  </>
);

export default RedirectInfo;
